// Add a customer manually from the /customers page header. Optional first
// contact captured in the same modal so the broker can log a new lead in
// one step. For the more common "new RFQ → create customer + contact +
// lines together" flow, see NewRfqModal.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import {
  api,
  type CreateCustomerRequest,
  type CreateCustomerResponse,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function NewCustomerModal({ open, onClose }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  // Customer fields
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [isFaaPart145, setIsFaaPart145] = useState(false);
  const [faaCertNumber, setFaaCertNumber] = useState('');
  const [isMilitary, setIsMilitary] = useState(false);
  const [carrierCertNumber, setCarrierCertNumber] = useState('');
  const [markupPctOverride, setMarkupPctOverride] = useState('');

  // Optional first contact
  const [includeContact, setIncludeContact] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactFullName, setContactFullName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactJobTitle, setContactJobTitle] = useState('');

  useEffect(() => {
    if (!open) {
      setLegalName('');
      setDisplayName('');
      setEmailDomain('');
      setCountryCode('');
      setIsFaaPart145(false);
      setFaaCertNumber('');
      setIsMilitary(false);
      setCarrierCertNumber('');
      setMarkupPctOverride('');
      setIncludeContact(false);
      setContactEmail('');
      setContactFullName('');
      setContactPhone('');
      setContactJobTitle('');
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: CreateCustomerRequest = { legalName: legalName.trim() };
      if (displayName.trim()) body.displayName = displayName.trim();
      if (emailDomain.trim()) body.emailDomain = emailDomain.trim().toLowerCase();
      if (countryCode.trim()) body.countryCode = countryCode.trim().toUpperCase();
      if (isFaaPart145) {
        body.isFaaPart145 = true;
        if (faaCertNumber.trim()) body.faaCertNumber = faaCertNumber.trim();
      }
      if (isMilitary) {
        body.isMilitary = true;
        if (carrierCertNumber.trim()) body.carrierCertNumber = carrierCertNumber.trim();
      }
      if (markupPctOverride.trim()) {
        const n = Number(markupPctOverride);
        if (Number.isFinite(n)) body.markupPctOverride = n;
      }
      if (includeContact && contactEmail.trim()) {
        body.contact = { email: contactEmail.trim() };
        if (contactFullName.trim()) body.contact.fullName = contactFullName.trim();
        if (contactPhone.trim()) body.contact.phone = contactPhone.trim();
        if (contactJobTitle.trim()) body.contact.jobTitle = contactJobTitle.trim();
      }
      return api<CreateCustomerResponse>('/api/customers', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setLocation(`/customers/${data.customer.id}`);
      onClose();
    },
  });

  const canSubmit =
    legalName.trim().length > 0 &&
    (!includeContact || contactEmail.trim().length > 0) &&
    !createMutation.isPending;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">Add customer</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Legal name *</div>
            <input
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="SFL General Trading LLC"
              maxLength={200}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
            />
          </label>

          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Display name (optional)
            </div>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Defaults to legal name"
              maxLength={200}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Email domain</div>
              <input
                value={emailDomain}
                onChange={(e) => setEmailDomain(e.target.value)}
                placeholder="sflgt.com"
                maxLength={253}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Country (ISO 2)</div>
              <input
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="AE"
                maxLength={2}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
          </div>

          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Markup % override (blank = use tenant default)
            </div>
            <input
              type="number"
              step="0.01"
              min={0}
              max={500}
              value={markupPctOverride}
              onChange={(e) => setMarkupPctOverride(e.target.value)}
              placeholder="e.g. 12.5"
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
            />
          </label>

          <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isFaaPart145}
                onChange={(e) => setIsFaaPart145(e.target.checked)}
              />
              FAA Part 145
            </label>
            {isFaaPart145 && (
              <input
                value={faaCertNumber}
                onChange={(e) => setFaaCertNumber(e.target.value)}
                placeholder="Cert #"
                maxLength={80}
                className="px-2 py-1 text-sm bg-background border border-border rounded font-mono w-40"
              />
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isMilitary}
                onChange={(e) => setIsMilitary(e.target.checked)}
              />
              Military / carrier
            </label>
            {isMilitary && (
              <input
                value={carrierCertNumber}
                onChange={(e) => setCarrierCertNumber(e.target.value)}
                placeholder="Carrier cert #"
                maxLength={80}
                className="px-2 py-1 text-sm bg-background border border-border rounded font-mono w-40"
              />
            )}
          </div>

          {/* Optional first contact */}
          <div className="border-t border-border pt-4 mt-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={includeContact}
                onChange={(e) => setIncludeContact(e.target.checked)}
              />
              Add a primary contact
            </label>
            {includeContact && (
              <div className="mt-3 space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Email *</div>
                  <input
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    type="email"
                    placeholder="najdi@sflgt.com"
                    maxLength={254}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground mb-1">Full name</div>
                    <input
                      value={contactFullName}
                      onChange={(e) => setContactFullName(e.target.value)}
                      maxLength={200}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground mb-1">Job title</div>
                    <input
                      value={contactJobTitle}
                      onChange={(e) => setContactJobTitle(e.target.value)}
                      maxLength={120}
                      placeholder="Procurement"
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                    />
                  </label>
                </div>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Phone</div>
                  <input
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    maxLength={40}
                    placeholder="+971 4 572 1032"
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
              </div>
            )}
          </div>

          {createMutation.isError && (
            <div className="text-sm text-danger">
              {(createMutation.error as { message?: string })?.message ?? 'Save failed'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating…' : 'Create customer'}
          </button>
        </div>
      </div>
    </div>
  );
}
